import React from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS } from '../../theme';
import { Skeleton, SkeletonAvatar } from '../common';

interface PostCardSkeletonProps {
  /**
   * Must match the entry the list is about to render: 'feed' is PostCard's
   * hairline-divided list row, 'card' its bordered grid tile, 'news' the
   * text-first NewsItem entry (kicker, headline, body lines — no avatar,
   * no media frame). Getting this wrong is the whole point of the
   * component — a skeleton in the other shape re-lays-out on load.
   */
  variant?: 'feed' | 'card' | 'news';
}

/**
 * Placeholder for a feed entry. Mirrors the real entry block for block —
 * same paddings, same edge treatment — so the list doesn't visibly
 * re-layout when the real posts land.
 */
export function PostCardSkeleton({ variant = 'feed' }: PostCardSkeletonProps) {
  const { colors } = useTheme();

  if (variant === 'news') {
    // Tracks NewsItem's styles — change them together.
    return (
      <View
        style={[
          styles.container,
          { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
        ]}>
        {/* Kicker — date · author overline */}
        <Skeleton width={130} height={10} borderRadius={BORDER_RADIUS.sm} />
        {/* Headline */}
        <Skeleton
          width="72%"
          height={15}
          borderRadius={BORDER_RADIUS.sm}
          style={styles.newsHeadline}
        />
        {/* Body lines */}
        <Skeleton width="94%" height={13} borderRadius={BORDER_RADIUS.sm} style={styles.caption} />
        <Skeleton
          width="58%"
          height={13}
          borderRadius={BORDER_RADIUS.sm}
          style={styles.captionTwo}
        />
        {/* Actions */}
        <View style={styles.actions}>
          <Skeleton width={15} height={15} borderRadius={BORDER_RADIUS.sm} />
          <Skeleton width={15} height={15} borderRadius={BORDER_RADIUS.sm} />
          <Skeleton width={15} height={15} borderRadius={BORDER_RADIUS.sm} />
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.container,
        variant === 'card'
          ? [styles.cardContainer, { borderColor: colors.border }]
          : { borderBottomWidth: 1, borderBottomColor: colors.border },
      ]}>
      {/* Header — avatar, name · time, category dot-label */}
      <View style={styles.header}>
        <SkeletonAvatar size={24} />
        <View style={styles.who}>
          <Skeleton width={140} height={12} borderRadius={BORDER_RADIUS.sm} />
        </View>
        <Skeleton width={62} height={11} borderRadius={BORDER_RADIUS.sm} />
      </View>

      {/* Media */}
      <Skeleton width="100%" height={0} borderRadius={BORDER_RADIUS.md} style={styles.media} />

      {/* Caption */}
      <Skeleton width="92%" height={13} borderRadius={BORDER_RADIUS.sm} style={styles.caption} />
      <Skeleton width="64%" height={13} borderRadius={BORDER_RADIUS.sm} style={styles.captionTwo} />

      {/* Meta line */}
      <Skeleton width="46%" height={11} borderRadius={BORDER_RADIUS.sm} style={styles.metaLine} />

      {/* Actions */}
      <View style={styles.actions}>
        <Skeleton width={16} height={16} borderRadius={BORDER_RADIUS.sm} />
        <Skeleton width={16} height={16} borderRadius={BORDER_RADIUS.sm} />
        <Skeleton width={16} height={16} borderRadius={BORDER_RADIUS.sm} />
      </View>
    </View>
  );
}

// Values track PostCard's styles — change them together.
const styles = StyleSheet.create({
  container: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.smd + 2,
    paddingBottom: SPACING.smd,
  },
  cardContainer: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  who: {
    flex: 1,
  },
  media: {
    marginTop: SPACING.sm + 2,
    aspectRatio: 4 / 3,
  },
  caption: {
    marginTop: SPACING.sm + 1,
  },
  captionTwo: {
    marginTop: SPACING.xs + 2,
  },
  metaLine: {
    marginTop: SPACING.sm,
  },
  newsHeadline: {
    marginTop: SPACING.sm,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SPACING.sm + 2,
    gap: SPACING.md + 2,
  },
});
